// Figma design:
//   Desktop: https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=17691-45880
//   Mobile: https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=17744-146938

import { Box, Stack, Typography } from '@mui/material';
import { ImageBox, TixlyIconButton, useIsMobile } from '@tix-ticketing/ui-components';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { pinToMinFontSize } from '@tix-ticketing/ui-theme';
import { useIntl } from 'react-intl';
import { DiscountedPrice } from 'components/common/price/discounted-price';
import { QuantitySelector } from 'components/common/quantity-selector/quantity-selector';
import { useProductMaxReached } from 'components/products/use-product-max-reached';
import { PriceWidthPlaceholder } from 'lib/helpers/formatted-price';
import type { OrderProductLine } from 'lib/helpers/product-helpers';

/** Whole digits a price beside the stepper is given room for, so the steppers
 * of every line stand in one column. */
const priceColumnDigits = 6;

/**
 * One product variant in the cart: artwork, name, category and chosen options,
 * with the combined price of its units. Given `onChangeQuantity`, the number of
 * units is a stepper, otherwise plain text; given `onRemove`, a button removes
 * them all.
 */
export function CartProductLine({
  line,
  inAside,
  onChangeQuantity,
  onRemove,
  maximum
}: {
  line: OrderProductLine;
  inAside: boolean;
  onChangeQuantity?: (quantity: number) => void;
  onRemove?: () => void;
  /** The most units the line can hold; `null` when unlimited. */
  maximum?: number | null;
}) {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const {
    maxReachedTitle,
    showMaxReachedToast
  } = useProductMaxReached();
  const compact = isMobile || inAside;
  const testId = `cart-product-line-${line.productVariantId}`;
  const quantity = onChangeQuantity ? <QuantitySelector value={line.quantity} minimum={1} maximum={maximum} onChange={onChangeQuantity} onMaxValueReached={() => showMaxReachedToast(maximum)} maxReachedLabel={maxReachedTitle} isSmall decrementTestId={`${testId}-decrement`} incrementTestId={`${testId}-increment`} /> : <Typography variant="body2Medium" data-testid={`${testId}-quantity`}>
      {line.quantity}
    </Typography>;
  const price = <DiscountedPrice price={line.priceWithTax} originalPrice={line.originalPrice} variant="body2Medium" originalVariant="body2" />;

  // The price and a hidden placeholder share one grid cell, so the column is
  // as wide as the longer of the two and the price sits at its right edge.
  const priceColumn = <Box sx={{
    display: 'grid'
  }}>
      <Typography aria-hidden variant="body2Medium" sx={{
      gridArea: '1 / 1',
      height: 0,
      visibility: 'hidden',
      whiteSpace: 'nowrap'
    }}>
        <PriceWidthPlaceholder digits={priceColumnDigits} />
      </Typography>
      <Box sx={{
      gridArea: '1 / 1'
    }}>{price}</Box>
    </Box>;

  // Styled as a ticket's remove button, beside which it sits.
  const removeButton = onRemove && <TixlyIconButton data-testid={`${testId}-remove`} color="monochrome" variant="contained" size="small" onClick={onRemove} aria-label={intl.formatMessage({
    id: "common.remove",
    defaultMessage: [{
      "type": 0,
      "value": "Remove"
    }]
  })} sx={{
    alignSelf: compact ? 'flex-start' : 'center',
    color: 'action.active',
    backgroundColor: 'transparent',
    '&:hover': {
      color: 'action.active'
    }
  }}>
      <CrossIcon />
    </TixlyIconButton>;
  return <Stack data-testid={testId} spacing={2} sx={theme => ({
    py: 2,
    pl: 2,
    pr: 1.5,
    backgroundColor: theme.palette.containerBackground.main,
    borderRadius: `${theme.formItemRadius.main}px`
  })}>
      <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: compact ? 2 : 3
    }}>
        <ImageBox imageUrl={line.imageUrl} altText="" variant="thumbnail" sx={{
        width: 64,
        height: 64,
        flexShrink: 0
      }} />
        <Stack sx={{
        flex: 1,
        minWidth: 0,
        alignSelf: 'center'
      }}>
          <Typography variant="body1Bold" sx={theme => ({
          overflowWrap: 'anywhere',
          ...(inAside && {
            fontSize: pinToMinFontSize(theme.typography.body1Bold.fontSize)
          })
        })}>
            {line.name}
          </Typography>
          {line.groupingText && <Typography variant="body3" sx={{
          color: 'text.secondary'
        }}>
              {line.groupingText}
            </Typography>}
          {line.variantName && <Typography variant="body3" sx={{
          mt: 0.5
        }}>
              {line.variantName}
            </Typography>}
        </Stack>
        {!compact ? <Box sx={{
        display: 'flex',
        alignItems: onChangeQuantity ? 'center' : 'flex-start',
        alignSelf: onChangeQuantity ? 'center' : 'flex-start',
        flexShrink: 0,
        gap: 1.5
      }}>
            {quantity}
            {priceColumn}
            {removeButton}
          </Box> : removeButton}
      </Box>
      {compact && <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2
    }}>
          {quantity}
          {price}
        </Box>}
    </Stack>;
}